import React, { useCallback, useEffect, useState } from 'react';
import { Box, Circle, Flex } from '@telescope/cassini-ui';
import { Text } from 'components';
import { Prize, AnimationOptions } from 'features';
import { WHEEL_CENTER_SIZE, WHEEL_SIZE, getSlotStyles, getTextStyles } from '../utils';
import { useTheme } from '@telescope/cassini-hooks';
import { CSSObject } from '@emotion/react';
import { motion } from 'framer-motion';

export interface SpinnerProps {
  /** array of spinner items */
  items: Prize[];

  /** index target slice to stop on */
  target: number;

  /** boolean to start and stop animation */
  animate: boolean;

  /** callback function execute on completion of the animation */
  onComplete?: Function;

  /** animation-specific configurations */
  animationOptions?: AnimationOptions;

  /** custom styles */
  styles: CSSObject

  children?: React.ReactNode
}

export const Spinner = ({ items, animate, onComplete, animationOptions, target, styles, children }: SpinnerProps) => {
  const { duration = 6000, ...options } = animationOptions ?? {};
  const { borderColor, ...customStyles } = styles;

  const count = items.length;
  const angles = 360 / count;

  const lastRotate = angles / 2;
  let getAngle = 360 - (angles + lastRotate);

  // get previous index
  let [prevIndex, setPrevIndex] = useState<number>(0);
  const [lastAngle, setLastAngle] = useState(getAngle);

  const numberOfTurns = duration / 1000;
  let targetIndex = target;

  const theme = useTheme();
  const { defaultColorScheme: c } = theme.settings;

  const getSpinValue = useCallback(() => {
    // checking previous target index
    let checkTargetIndex = prevIndex !== targetIndex ? targetIndex - prevIndex : 0;
    let spinningVal = numberOfTurns * 360 + (-360 * checkTargetIndex) / items.length;
    spinningVal = spinningVal + lastAngle;

    return spinningVal;
  }, [targetIndex, prevIndex, lastAngle, items])

  useEffect(() => {
    if (!animate) return;
    
    setPrevIndex(targetIndex);
    const value = getSpinValue();
    setLastAngle(value);

    setTimeout(() => {
      if (onComplete) onComplete();
    }, duration);
  }, [animate]);

  return (
    <Circle position="relative" size={{...WHEEL_SIZE}} sx={{ ...customStyles }}>
        <motion.div
          initial={false} 
          animate={{ rotate: lastAngle }}
          transition={{ 
            duration: numberOfTurns,
            ease: 'easeInOut',
            ...options
          }}
          style={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            border: `.2em solid ${borderColor}`,
            borderRadius: '50%'
          }}
        >
          {items.map((item: Prize, index: number) => {
            const slotStyle = getSlotStyles(items.length, index);
            const textStyles = getTextStyles(items.length);
            const bg = (index % 2)? `${c}.700` : `${c}.800`;

            return (
              <Box key={index} 
                position="absolute" w="50%" left="50%"
                color="chakra-body-text" bg={bg}  border={`0.1em solid ${borderColor}`} borderRight="none" borderTop="none"
                sx={{ ...slotStyle, ...item.styles }}>
                <Box className="Wheel__text-container" position="absolute" w="100%" left="-50%" textAlign="center" sx={{...textStyles}}>
                  <Text
                    display="inline-flex"
                    wordBreak="break-word"
                    maxW="50%"
                    h="16%"
                    alignItems="center"
                    fontWeight="500"
                    fontSize={['sm', 'sm', 'md', 'lg']} 
                    allowMarkdown> 
                    {item.copy} 
                  </Text>
                </Box>
              </Box>
            );
          })}
        </motion.div>

      <Flex as={Circle} bg="chakra-body-bg" 
            alignItems="center" justifyItems="center" size={WHEEL_CENTER_SIZE} 
            position="absolute"
            border={`.2em solid ${borderColor}`}
            sx={{ '> *': { position: 'absolute' } }}>
        {children}
      </Flex>
    </Circle>
  );
};
